import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient'; 
import { useAuth } from '@/contexts/AuthContext';
 
import { Button } from '@/components/ui/button'; 
import { Input } from '@/components/ui/input'; 
import Papa from 'papaparse';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Modal } from '@/components/ui/Modal'; 
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"; 
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,  
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu"; 
import { format, parseISO } from 'date-fns';
import { 
  FileText,
  PlayCircle,
  ClipboardList,
  MoreVertical,
  Newspaper,
  ThumbsUp,
  ThumbsDown,
  PhoneOff
} from 'lucide-react';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { Toaster, toast } from 'sonner';
import { InfoModalTrigger } from '@/components/ui/InfoModalTrigger';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label'; // Added Label import
import { useVideoBySlug } from '@/hooks/useVideos';
import VideoLink from '@/components/ui/VideoLink';

type CallLog = {
  id: string;
  user_id: string;
  vapi_call_id: string;
  customer_phone_number: string | null;
  call_started_reason: string | null; 
  vapi_ended_reason: string | null;
  call_outcome: string | null; 
  start_time: string | null; 
  end_time: string | null; 
  duration_seconds: number | null;
  summary: string | null;
  transcript: string | null;
  recording_url: string | null;
  created_at: string; 
  appointment_datetime_iso: string | null;
  booking_status: 'Confirmed' | 'Tentative' | null;
  appointment_type: string | null;
  customer_full_name: string | null;
  customer_email: string | null;
  customer_phone_from_analysis: string | null;
  customer_sentiment: 'positive' | 'neutral' | 'negative' | 'mixed' | null;
  key_topics_discussed: string[] | null; 
  next_action_required_by_staff: string | null;
  raw_structured_data: Record<string, unknown> | null; 
  feedback_rating: number | null;
  feedback_comment: string | null;
};

type SortField = keyof CallLog | null;
type SortDirection = 'asc' | 'desc';

const ITEMS_PER_PAGE = 15;
const DEBOUNCE_DELAY = 500; 
const LUCIDE_ICON_SIZE = 18; 
const LUCIDE_ICON_STROKE_WIDTH = 2;
const FEEDBACK_ZAPIER_WEBHOOK_URL = 'https://hooks.zapier.com/hooks/catch/7132591/2vr2g80/';

export default function CallsPage() {
  const { user } = useAuth();
  const CallLogsOverviewVideo = useVideoBySlug('call-logs-overview');
  const HowToExportCallDataVideo = useVideoBySlug('export-call-data');
  const [searchParams] = useSearchParams();
  const highlightCallId = searchParams.get('highlight');
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate] = useState<Date | null>(null);
  const [endDate] = useState<Date | null>(null);
  const [outcomeFilter, setOutcomeFilter] = useState<string>('__ALL__');
  const [reasonFilter, setReasonFilter] = useState<string>('__ALL__');
  const [sortField, setSortField] = useState<SortField>('start_time');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsModalContent, setDetailsModalContent] = useState<{ title: string; content: React.ReactNode }>({ title: '', content: null });
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackRating, setFeedbackRating] = useState<1 | -1 | null>(null);
  const [feedbackLogId, setFeedbackLogId] = useState<string | null>(null);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [phoneToBlock, setPhoneToBlock] = useState<string | null>(null);
  const [blockReason, setBlockReason] = useState<string>('customer_requested');
  const [blockNotes, setBlockNotes] = useState<string>('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0); 
    }, DEBOUNCE_DELAY);
    return () => clearTimeout(handler);
  }, [searchTerm]);



  const reasonOptions = useMemo(() => [
    { value: '__ALL__', label: 'All Reasons' },
    { value: 'Inbound Call', label: 'Inbound Call' },
    { value: 'Web Call', label: 'Web Call' },
    { value: 'API', label: 'API' },
    { value: 'Appointment Confirmation', label: 'Appointment Confirmation' },
    { value: 'Cancelled Appointment Recovery', label: 'Cancelled Recovery' },
    { value: 'No-Show Follow-up', label: 'No-Show Follow-up' },
    { value: 'Manual Upload', label: 'Manual Upload' },
    { value: 'Unknown', label: 'Unknown' },
  ], []);

  const outcomeOptions = useMemo(() => [
    { value: '__ALL__', label: 'All Outcomes' },
    { value: 'Appointment Scheduled', label: 'Appointment Scheduled' },
    { value: 'Appointment Rescheduled', label: 'Appointment Rescheduled' },
    { value: 'Appointment Confirmed', label: 'Appointment Confirmed' },
    { value: 'Appointment Cancelled', label: 'Appointment Cancelled' },
    { value: 'Left Voicemail', label: 'Left Voicemail' },
    { value: 'No Answer', label: 'No Answer' },
    { value: 'Call Failed', label: 'Call Failed' },
    { value: 'Information Provided', label: 'Information Provided' },
    { value: 'Transferred', label: 'Transferred' },
    { value: 'Hung Up', label: 'Hung Up' },
    { value: 'Other', label: 'Other' },
  ], []);

  const fetchCallLogs = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const from = page * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;
      let query = supabase
        .from('call_logs')
        .select('*', { count: 'exact' }) 
        .eq('user_id', user.id);

      if (debouncedSearchTerm) {
        query = query.or(
          `customer_phone_number.ilike.%${debouncedSearchTerm}%,` +
          `summary.ilike.%${debouncedSearchTerm}%,` +
          `transcript.ilike.%${debouncedSearchTerm}%,` + 
          `customer_full_name.ilike.%${debouncedSearchTerm}%,` + 
          `customer_email.ilike.%${debouncedSearchTerm}%`
        );
      }

      if (startDate) {
        query = query.gte('start_time', startDate.toISOString());
      }
      if (endDate) {
        const adjustedEndDate = new Date(endDate);
        adjustedEndDate.setDate(adjustedEndDate.getDate() + 1); 
        query = query.lt('start_time', adjustedEndDate.toISOString());
      }
      if (reasonFilter && reasonFilter !== '__ALL__') {
        query = query.eq('call_started_reason', reasonFilter);
      }
      if (outcomeFilter && outcomeFilter !== '__ALL__') {
        query = query.eq('call_outcome', outcomeFilter);
      }
      if (sortField) {
        query = query.order(sortField, { ascending: sortDirection === 'asc', nullsFirst: false });
      } else {
         query = query.order('start_time', { ascending: false, nullsFirst: false }); 
      }
      query = query.range(from, to);
      const { data, error: fetchError, count } = await query;
      if (fetchError) throw fetchError;
      setCallLogs(data as CallLog[] || []); 
      setTotalCount(count || 0);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      console.error("Error fetching call logs:", err);
      setError(`Failed to load call logs: ${errorMessage}`);
      toast.error(`Failed to load call logs: ${errorMessage}`);
      setCallLogs([]); 
      setTotalCount(0);  
    } finally {
      setLoading(false);
    }
  }, [user, page, debouncedSearchTerm, startDate, endDate, outcomeFilter, reasonFilter, sortField, sortDirection]);

  useEffect(() => {
    fetchCallLogs();
  }, [fetchCallLogs]);

  const handleSort = (field: SortField) => {
    if (!field) return;
    const newDirection = sortField === field && sortDirection === 'desc' ? 'asc' : 'desc';
    setSortField(field);
    setSortDirection(newDirection);
    setPage(0); 
  };

  const formatDuration = (seconds: number | null): string => {
    if (seconds === null || seconds < 0) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'MMM d, yyyy HH:mm');
    } catch {
      return 'Invalid Date';
    }
  };

  const exportCsv = async () => {
    if (!user) return;
    toast.info("Exporting CSV...");
    try {
      let query = supabase
        .from('call_logs')
        .select('*') 
        .eq('user_id', user.id);

      if (debouncedSearchTerm) {
        query = query.or(
          `customer_phone_number.ilike.%${debouncedSearchTerm}%,` +
          `summary.ilike.%${debouncedSearchTerm}%,` +
          `transcript.ilike.%${debouncedSearchTerm}%,` +
          `customer_full_name.ilike.%${debouncedSearchTerm}%,` +
          `customer_email.ilike.%${debouncedSearchTerm}%`
        );
      }
      if (startDate) query = query.gte('start_time', startDate.toISOString());
      if (endDate) {
        const adjustedEndDate = new Date(endDate);
        adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
        query = query.lt('start_time', adjustedEndDate.toISOString());
      }
      if (reasonFilter && reasonFilter !== '__ALL__') query = query.eq('call_started_reason', reasonFilter);
      if (outcomeFilter && outcomeFilter !== '__ALL__') query = query.eq('call_outcome', outcomeFilter);
      
      if (sortField) query = query.order(sortField, { ascending: sortDirection === 'asc', nullsFirst: false });
      else query = query.order('start_time', { ascending: false, nullsFirst: false });
      
      const { data: rawData, error: RpcError } = await query; 
      if (RpcError) throw RpcError;
      if (!rawData || rawData.length === 0) {
        toast.warning("No data to export with current filters.");
        return;
      }
      
      const transformedData = rawData.map(log => {
        const {
          vapi_ended_reason,
          key_topics_discussed,
          raw_structured_data,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          vapi_call_id: _vapi_call_id,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          recording_url: _recording_url,
          ...restOfLog 
        } = log as CallLog;
        
        const csvRow: Record<string, unknown> = { ...restOfLog }; 
        
        csvRow.ended_reason = vapi_ended_reason !== null ? vapi_ended_reason : ''; 
        csvRow.key_topics_discussed = Array.isArray(key_topics_discussed) && key_topics_discussed.length > 0 ? key_topics_discussed.join('; ') : ''; 
        csvRow.raw_structured_data = raw_structured_data && typeof raw_structured_data === 'object' && Object.keys(raw_structured_data).length > 0 ? JSON.stringify(raw_structured_data) : ''; 
        
        return csvRow;
      });
      const csv = Papa.unparse(transformedData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'call_logs.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("CSV export successful!");
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      console.error('CSV export failed', err);
      toast.error(`CSV export failed: ${errorMessage}`);
      setError(`CSV export failed: ${errorMessage}`); 
    }
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const showSummaryModal = (log: CallLog) => {
    setDetailsModalContent({
      title: `Summary for call to ${log.customer_phone_number || 'Unknown'}`,
      content: <p className="text-sm text-gray-700 whitespace-pre-wrap">{log.summary || "No summary available."}</p>
    });
    setDetailsModalOpen(true);
  }

  const showTranscriptModal = (log: CallLog) => {
    setDetailsModalContent({
      title: `Transcript for call to ${log.customer_phone_number || 'Unknown'}`,
      content: <pre className="text-sm text-gray-600 whitespace-pre-wrap font-sans max-h-[60vh] overflow-y-auto">{log.transcript || "No transcript available."}</pre>
    });
    setDetailsModalOpen(true);
  }

  const playRecordingModal = (log: CallLog) => {
    setDetailsModalContent({
      title: `Recording for call to ${log.customer_phone_number || 'Unknown'}`,
      content: log.recording_url ? (
        <audio controls src={log.recording_url} className="w-full">
          Your browser does not support the audio element. <a href={log.recording_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Download Recording</a>
        </audio>
      ) : (
        <p className="text-sm text-gray-600">No recording available for this call.</p>
      )
    });
    setDetailsModalOpen(true);
  }

  const showStructuredDataDetailsModal = (log: CallLog) => {
    const detailsToShow: {label: string, value: string | string[] | null, isList?: boolean}[] = [
      { label: "Appointment Date/Time (ISO)", value: log.appointment_datetime_iso ? formatDate(log.appointment_datetime_iso) : null },
      { label: "Appointment Type", value: log.appointment_type },
      { label: "Customer Full Name", value: log.customer_full_name },
      { label: "Customer Email", value: log.customer_email },
      { label: "Customer Phone", value: log.customer_phone_from_analysis },
      { label: "Customer Sentiment", value: log.customer_sentiment },
      { label: "Key Topics Discussed", value: log.key_topics_discussed, isList: true },
      { label: "Next Action Required by Staff", value: log.next_action_required_by_staff },
    ];
    setDetailsModalContent({
      title: `Customer Details for call to ${log.customer_phone_number || 'Unknown'}`,
      content: (
        <div className="space-y-3 text-sm">
          {detailsToShow.map((item, index) => {
            if (item.value === null || (typeof item.value === 'string' && item.value.trim() === '')) {
              if (item.isList && (item.value === null || (Array.isArray(item.value) && item.value.length === 0))) {
                 // Fall through
              } else if (!item.isList) {
                return null; 
              }
            }
            if (item.isList) {
              const topics = item.value as string[] | null;
              return (
                <div key={index}>
                  <p className="font-semibold text-gray-800">{item.label}:</p>
                  {topics && topics.length > 0 ? (
                    <ul className="list-disc list-inside pl-4 text-gray-700">
                      {topics.map((topic, idx) => <li key={idx}>{topic}</li>)}
                    </ul>
                  ) : (
                    <p className="text-gray-600 italic">No {item.label.toLowerCase()} recorded.</p>
                  )}
                </div>
              );
            }
            return (
              <div key={index}>
                <span className="font-semibold text-gray-800">{item.label}:</span>{' '}
                <span className="text-gray-700">{item.value}</span>
              </div>
            );
          })}
        </div>
      )
    });
    setDetailsModalOpen(true);
  };

  const openFeedbackModal = (logId: string, rating: 1 | -1, currentComment: string | null) => {
    setFeedbackLogId(logId);
    setFeedbackRating(rating);
    setFeedbackComment(currentComment || '');
    setFeedbackModalOpen(true);
  };

  const openBlockNumberModal = (phone: string) => {
    setPhoneToBlock(phone);
    setBlockReason('customer_requested'); // Reset to default when opening
    setBlockNotes(''); // Reset to default when opening
    setBlockModalOpen(true);
  };

  const blockNumber = async () => {
    if (!phoneToBlock) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/do-not-call-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ 
          phone_number: phoneToBlock,
          reason: blockReason,
          notes: blockNotes.trim() || undefined
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to add to DNC');
      }
      
      toast.success(`${phoneToBlock} added to Do Not Call list`);
      setBlockReason('customer_requested'); // Reset form state
      setBlockNotes('');
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'An unknown error occurred';
      toast.error(errorMessage);
    } finally {
      setBlockModalOpen(false); // Close modal in finally block
    }
  };

  const submitFeedback = async () => {
    if (!feedbackLogId || feedbackRating === null) {
      toast.error('Feedback log ID or rating is missing.');
      setFeedbackModalOpen(false);
      return;
    }
    setIsSubmittingFeedback(true);
    setError(null); 
    try {
      const { data, error: functionInvokeError } = await supabase.functions.invoke('call-feedback', {
        body: {
          log_id: feedbackLogId,
          rating: feedbackRating,
          comment: feedbackComment.trim() === '' ? null : feedbackComment.trim(), 
        },
      });

      if (functionInvokeError) {
        console.error('Failed to save feedback via function (invocation error):', functionInvokeError);
        const message = functionInvokeError.message || 'An unknown error occurred during function invocation.';
        toast.error(`Feedback submission failed: ${message}`);
        setError(`Feedback submission failed: ${message}`); 
      } else if (data && data.success === true && data.updatedRecord) {
        setCallLogs((logs) =>
          logs.map((l) =>
            l.id === feedbackLogId ? { ...l, ...data.updatedRecord } : l
          )
        );
        toast.success('Feedback submitted successfully!');
        
        try {
          const log = callLogs.find(l => l.id === feedbackLogId);
          const payload = new URLSearchParams({
            user_email: user?.email ?? '',
            log_id: feedbackLogId,
            rating: feedbackRating === 1 ? 'Thumbs Up' : 'Thumbs Down',
            comment: feedbackComment || '',
            timestamp: new Date().toISOString(),
            customer_phone: log?.customer_phone_number || '',
            call_reason: log?.call_started_reason || '',
            call_outcome: log?.call_outcome || '',
            duration: log?.duration_seconds?.toString() || '',
          });
          await fetch(FEEDBACK_ZAPIER_WEBHOOK_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: payload.toString(),
          });
        } catch (zapErr) {
          console.error('Failed to send feedback to Zapier:', zapErr);
        }
      } else {
        const errorMessage = data?.error || 'Feedback submission failed: Unexpected response from server.';
        console.error('Feedback submission was not successful or returned unexpected data:', data);
        toast.error(errorMessage);
        setError(errorMessage);
      }
    } catch (invokeCatchError: unknown) {
      const errorMessage = invokeCatchError instanceof Error ? invokeCatchError.message : 'An unknown error occurred';
      console.error('Error during feedback submission process (catch block):', invokeCatchError);
      toast.error(`Feedback submission failed: ${errorMessage}`);
      setError(`Feedback submission failed: ${errorMessage}`);
    } finally {
      setIsSubmittingFeedback(false);
      setFeedbackModalOpen(false);
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field || !field) return <FaSort className="inline-block ml-1 h-3 w-3 text-gray-400" />;
    return sortDirection === 'asc' ? <FaSortUp className="inline-block ml-1 h-3 w-3" /> : <FaSortDown className="inline-block ml-1 h-3 w-3" />;
  };

  const callLogsInfoContent = (
    <>
      <div className="mb-4">
        <p className="mb-4 text-gray-700">
          The Call Logs page shows all calls your AI employee was involved in - both calls it initiated and calls it received. 
          Use the filters and search to find specific calls, and export data for further analysis.
        </p>
        
        <div className="space-y-6">
          {CallLogsOverviewVideo && <div>
            <h4 className="font-bold mb-2">1. Call Logs Overview</h4>
            <p className="text-sm text-gray-600 mb-3">Learn how to navigate and filter your call history</p>

              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={CallLogsOverviewVideo || "https://www.loom.com/embed/85c852abb53c47c8bfc80d9e87cf7aea?sid=4945c455-d35b-444f-81ed-05d9d7924e63"}
                  frameBorder="0"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                />
              </div>
              <VideoLink to="call-logs-overview" />
          </div>}

          {HowToExportCallDataVideo && <div>
            <h4 className="font-bold mb-2">2. How to Export Call Data</h4>
            <p className="text-sm text-gray-600 mb-3">Step-by-step guide to export filtered call data to CSV</p>
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={HowToExportCallDataVideo ||"https://www.loom.com/embed/8ddc1ecc534d43fdb5b2d861f11f4078?sid=405cdf37-92b9-4e40-b8e4-c0e63fb5e216"}
                frameBorder="0"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full rounded-lg"
              />
            </div>
            <VideoLink to="export-call-data" />
          </div>}
        </div>

        <div className="mt-6 space-y-3">
          <div>
            <h4 className="font-medium mb-1">Key Features:</h4>
            <ul className="text-sm space-y-1 text-gray-600">
              <li><strong>Filter by Reason:</strong> Inbound calls, web calls, appointment confirmations, manual uploads</li>
              <li><strong>Filter by Outcome:</strong> Appointment scheduled, voicemail, no answer, call failed</li>
              <li><strong>Search:</strong> Find calls by customer name, phone number, or keywords in summaries/transcripts</li>
              <li><strong>Sort:</strong> Click column headers to sort by phone number, reason, outcome, start time, or duration</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium mb-1">Call Actions:</h4>
            <ul className="text-sm space-y-1 text-gray-600">
              <li><strong>View Summary:</strong> AI-generated call summaries</li>
              <li><strong>Play Recording:</strong> Listen to call audio recordings</li>
              <li><strong>Customer Details:</strong> Extracted data like names, topics, sentiment</li>
              <li><strong>View Transcript:</strong> Full text conversation</li>
              <li><strong>Feedback:</strong> Help improve AI with thumbs up/down</li>
              <li><strong>Block Number:</strong> Add to Do Not Call list</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
  
  const callLogActionsInfoContent = (
    <>
      <p className="mb-2">Understand the actions available for each call log:</p>
      <ul className="list-disc list-inside space-y-2 pl-4">
        <li><strong className="inline-flex items-center"><FileText size={16} className="mr-1.5 text-blue-600" /> View Summary:</strong> Displays an AI-generated summary of the call conversation.</li>
        <li><strong className="inline-flex items-center"><PlayCircle size={16} className="mr-1.5 text-red-600" /> Play Recording:</strong> Listen to the audio recording of the call (if available).</li>
        <li><strong className="inline-flex items-center"><ClipboardList size={16} className="mr-1.5 text-purple-600" /> View Customer Details:</strong> Shows structured data extracted by the AI, such as appointment details, customer information, and key topics.</li>
        <li><strong className="inline-flex items-center"><Newspaper size={16} className="mr-1.5 text-gray-700" /> View Transcript (in "⋮ More"):</strong> Shows the full text transcript of the call.</li>
        <li><strong className="inline-flex items-center">Help Improve (<ThumbsUp size={16} className="mx-1 text-green-600" /> / <ThumbsDown size={16} className="mx-1 text-red-600" /> in "⋮ More"):</strong> Provide feedback on the call's outcome or AI performance to help us improve the system. Your feedback is valuable!</li>
      </ul>
    </>
  );

  return (
    <div className="space-y-6 p-4 md:p-6">
      <Toaster richColors position="top-right" />
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-gray">Call Logs</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv}>
            Export CSV
          </Button>
          <InfoModalTrigger modalTitle="Call Logs Guide" modalContent={callLogsInfoContent} />
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg shadow-sm border border-gray-200">
        <Input
          placeholder="Search customer, summary..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="sm:col-span-2 lg:col-span-2"
        />
        <Select value={reasonFilter} onValueChange={(value) => { setReasonFilter(value); setPage(0); }}>
          <SelectTrigger><SelectValue placeholder="Filter by Reason" /></SelectTrigger>
          <SelectContent>
            {reasonOptions.map(option => (<SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={outcomeFilter} onValueChange={(value) => { setOutcomeFilter(value); setPage(0); }}>
          <SelectTrigger><SelectValue placeholder="Filter by Outcome" /></SelectTrigger>
          <SelectContent>
            {outcomeOptions.map(option => (<SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>
      
      {error && !loading && ( 
        <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
          <span className="font-medium">Error:</span> {error}
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-slate-gray">Recorded Calls</h2>
            <InfoModalTrigger modalTitle="Call Log Actions Guide" modalContent={callLogActionsInfoContent} />
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead onClick={() => handleSort('customer_phone_number')} className="cursor-pointer whitespace-nowrap">Customer # <SortIcon field="customer_phone_number" /></TableHead>
                <TableHead onClick={() => handleSort('call_started_reason')} className="cursor-pointer whitespace-nowrap">Call Reason <SortIcon field="call_started_reason" /></TableHead>
                <TableHead onClick={() => handleSort('call_outcome')} className="cursor-pointer whitespace-nowrap">Outcome <SortIcon field="call_outcome" /></TableHead>
                <TableHead onClick={() => handleSort('start_time')} className="cursor-pointer whitespace-nowrap">Started <SortIcon field="start_time" /></TableHead>
                <TableHead onClick={() => handleSort('duration_seconds')} className="cursor-pointer whitespace-nowrap">Duration <SortIcon field="duration_seconds" /></TableHead>
                <TableHead className="whitespace-nowrap text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-gray-500">Loading call logs...</TableCell></TableRow>
              ) : callLogs.length === 0 && !error ? ( 
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-gray">No call logs found.</TableCell></TableRow>
              ) : (
                callLogs.map((log) => (
                  <TableRow 
                    key={log.id} 
                    className={`hover:bg-gray-50 ${
                      highlightCallId === log.id 
                        ? "bg-orange-100 border-l-4 border-orange-400 animate-pulse" 
                        : ""
                    }`}
                  >
                    <TableCell className="font-medium whitespace-nowrap">{log.customer_phone_number || 'N/A'}</TableCell>
                    <TableCell className="whitespace-nowrap">{log.call_started_reason || 'N/A'}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {log.call_outcome === "Appointment Scheduled" && log.booking_status === "Tentative" ? (
                        <span className="px-2 py-0.5 rounded-md bg-orange-100 text-orange-700 font-medium text-xs sm:text-sm">{log.call_outcome} (Tentative)</span>
                      ) : ( log.call_outcome || 'N/A' )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{formatDate(log.start_time)}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatDuration(log.duration_seconds)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center space-x-1 md:space-x-1.5">
                        <Button variant="ghost" size="icon" onClick={() => showSummaryModal(log)} disabled={!log.summary} title="View Summary" className="p-1 h-auto text-blue-600 disabled:text-gray-400 hover:bg-blue-50 disabled:hover:bg-transparent">
                          <FileText size={LUCIDE_ICON_SIZE} strokeWidth={LUCIDE_ICON_STROKE_WIDTH} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => playRecordingModal(log)} disabled={!log.recording_url} title="Play Recording" className="p-1 h-auto text-red-600 disabled:text-gray-400 hover:bg-red-50 disabled:hover:bg-transparent">
                          <PlayCircle size={LUCIDE_ICON_SIZE} strokeWidth={LUCIDE_ICON_STROKE_WIDTH} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => showStructuredDataDetailsModal(log)} title="View Customer Details" className="p-1 h-auto text-purple-600 disabled:text-gray-400 hover:bg-purple-50 disabled:hover:bg-transparent">
                          <ClipboardList size={LUCIDE_ICON_SIZE} strokeWidth={LUCIDE_ICON_STROKE_WIDTH} />
                        </Button>
                        
                        <DropdownMenu modal={false}> 
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="p-1 h-auto text-gray-600 hover:bg-gray-100" title="More actions">
                              <MoreVertical size={LUCIDE_ICON_SIZE} strokeWidth={LUCIDE_ICON_STROKE_WIDTH} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onSelect={(e) => {
                                if (!log.transcript) { e.preventDefault(); return; } 
                                showTranscriptModal(log);
                              }} 
                              disabled={!log.transcript}
                            >
                              <Newspaper size={LUCIDE_ICON_SIZE-2} strokeWidth={LUCIDE_ICON_STROKE_WIDTH} className="mr-2" />
                              View Transcript
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuSub>
                                <DropdownMenuSubTrigger>
                                    Help Improve AI
                                </DropdownMenuSubTrigger>
                                <DropdownMenuPortal>
                                    <DropdownMenuSubContent>
                                        <DropdownMenuItem 
                                          onSelect={() => openFeedbackModal(log.id, 1, log.feedback_comment)} 
                                          className={`${log.feedback_rating === 1 ? 'bg-green-50 text-green-700' : ''}`}
                                        >
                                            <ThumbsUp size={LUCIDE_ICON_SIZE-2} strokeWidth={LUCIDE_ICON_STROKE_WIDTH} className={`mr-2 ${log.feedback_rating === 1 ? 'text-green-600' : ''}`} />
                                            Thumbs Up
                                        </DropdownMenuItem>
                                        <DropdownMenuItem 
                                          onSelect={() => openFeedbackModal(log.id, -1, log.feedback_comment)} 
                                          className={`${log.feedback_rating === -1 ? 'bg-red-50 text-red-700' : ''}`}
                                        >
                                            <ThumbsDown size={LUCIDE_ICON_SIZE-2} strokeWidth={LUCIDE_ICON_STROKE_WIDTH} className={`mr-2 ${log.feedback_rating === -1 ? 'text-red-600' : ''}`} />
                                            Thumbs Down
                                        </DropdownMenuItem>
                            </DropdownMenuSubContent>
                        </DropdownMenuPortal>
                    </DropdownMenuSub>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onSelect={() => log.customer_phone_number && openBlockNumberModal(log.customer_phone_number)}
                      disabled={!log.customer_phone_number}
                      className="text-red-600 focus:text-red-700 focus:bg-red-50"
                    >
                      <PhoneOff size={LUCIDE_ICON_SIZE-2} strokeWidth={LUCIDE_ICON_STROKE_WIDTH} className="mr-2" />
                      Add to Do Not Call
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </TableCell>
          </TableRow>
        ))
      )}
    </TableBody>
  </Table>
</div>
        
        {totalCount > ITEMS_PER_PAGE && (
          <div className="flex items-center justify-between p-4 border-t border-gray-200">
            <span className="text-sm text-gray-700">Page {page + 1} of {totalPages} ({totalCount} logs)</span>
            <div className="space-x-2">
              <Button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0 || loading} variant="outline" size="sm">Previous</Button>
              <Button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1 || loading} variant="outline" size="sm">Next</Button>
            </div>
          </div>
        )}
      </div>
      
      <Modal isOpen={detailsModalOpen} onOpenChange={setDetailsModalOpen} title={detailsModalContent.title}>
        {detailsModalContent.content}
      </Modal>
      
      <Modal
        isOpen={feedbackModalOpen}
        onOpenChange={(isOpen) => { if (isSubmittingFeedback && !isOpen) return; setFeedbackModalOpen(isOpen); }} 
        title={feedbackRating === 1 ? 'Give Positive Feedback' : 'Give Negative Feedback'}
      >
        <div className="p-4 space-y-4"> 
          <Textarea placeholder="Optional comments..." value={feedbackComment} onChange={(e) => setFeedbackComment(e.target.value)} rows={4} disabled={isSubmittingFeedback} />
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setFeedbackModalOpen(false)} disabled={isSubmittingFeedback}>Cancel</Button>
            <Button onClick={submitFeedback} className="bg-primary hover:bg-primary/90 text-primary-foreground" disabled={isSubmittingFeedback}>
              {isSubmittingFeedback ? 'Saving...' : 'Save Feedback'}
            </Button>
          </div>
        </div>
      </Modal>

      <AlertDialog open={blockModalOpen} onOpenChange={setBlockModalOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Add to Do Not Call List?</AlertDialogTitle>
            <AlertDialogDescription>
              Adding {phoneToBlock} will prevent your AI Employee from calling this number in the future. This action can be reversed in Settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="block-reason-calls">Reason for blocking</Label>
              <Select value={blockReason} onValueChange={setBlockReason}>
                <SelectTrigger id="block-reason-calls">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer_requested">Customer requested</SelectItem>
                  <SelectItem value="wrong_number">Wrong number</SelectItem>
                  <SelectItem value="competitor">Competitor</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="block-notes-calls">Notes (optional)</Label>
              <Textarea
                id="block-notes-calls"
                placeholder="Add any additional context..."
                value={blockNotes}
                onChange={(e) => setBlockNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setBlockModalOpen(false);
              setBlockReason('customer_requested');
              setBlockNotes('');
            }}>
              Keep Active
            </AlertDialogCancel>
            <Button variant="destructive" onClick={blockNumber}>
              Add to List
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}